import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, ShieldCheck, Sparkles, CheckCircle2, Gauge, Lock, ScrollText } from 'lucide-react'
import UserLogin from './UserLogin'

const blobs = [
  {
    className: 'absolute -left-20 top-12 h-80 w-80 rounded-full bg-blue-600/25 blur-3xl',
    animate: { x: [0, 70, -30, 0], y: [0, -25, 20, 0] },
    duration: 26,
  },
  {
    className: 'absolute right-[-80px] top-[-40px] h-96 w-96 rounded-full bg-violet-700/20 blur-3xl',
    animate: { x: [0, -55, 25, 0], y: [0, 30, -20, 0] },
    duration: 30,
  },
  {
    className: 'absolute bottom-[-120px] left-1/3 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl',
    animate: { x: [0, 40, -20, 0], y: [0, -30, 25, 0] },
    duration: 28,
  },
  {
    className: 'absolute bottom-8 right-24 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl',
    animate: { x: [0, -35, 10, 0], y: [0, 15, -20, 0] },
    duration: 34,
  },
]

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

export default function Landing({ onLoginSuccess, onLogout, isAuthenticated, userCuit, isAdmin, onGoToAdmin }) {
  const [cardTilt, setCardTilt] = useState({ rotateX: 0, rotateY: 0 })

  const onCardMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * 100
    const y = ((event.clientY - rect.top) / rect.height) * 100
    const offsetX = (x - 50) / 50
    const offsetY = (y - 50) / 50
    setCardTilt({ rotateX: Number((-offsetY * 5).toFixed(2)), rotateY: Number((offsetX * 6).toFixed(2)) })
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-transparent">
      {blobs.map((blob, index) => (
        <motion.div
          key={index}
          className={`${blob.className} z-0`}
          animate={blob.animate}
          transition={{ duration: blob.duration, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      <div className="pointer-events-none absolute inset-0 z-[2] backdrop-blur-3xl" />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-20 mx-auto max-w-7xl px-5 py-12 sm:px-8 md:py-16"
      >
        <section className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <motion.div variants={fadeUp}>
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-slate-200">
              <ShieldCheck className="h-3.5 w-3.5 text-cyan-300" /> Motor pericial previsional
            </p>
             <h1 className="text-4xl font-bold tracking-tight text-white md:text-6xl">
               Inteligencia Forense
              <span className="profile-title-gradient block bg-clip-text text-transparent">
                para Pericias Previsionales
              </span>
            </h1>
            <p className="mt-4 max-w-xl text-slate-300">
              Procesa historias laborales de ANSES con trazabilidad completa, deteccion de gaps y estructura lista para liquidacion judicial.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mx-auto w-[90%] max-w-xl lg:w-full">
            <motion.div
              animate={cardTilt}
              transition={{ type: 'spring', stiffness: 220, damping: 20 }}
              className="group relative rounded-3xl border border-white/20 bg-white/10 p-5 shadow-[0_20px_50px_rgba(0,0,0,0.3)] backdrop-blur-2xl md:p-8"
              style={{ transformStyle: 'preserve-3d' }}
              onMouseMove={onCardMove}
              onMouseLeave={() => {
                setCardTilt({ rotateX: 0, rotateY: 0 })
              }}
            >
              <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-cyan-300/0 transition-all duration-300 group-hover:ring-cyan-300/30" />
              <UserLogin
                onLoginSuccess={onLoginSuccess}
                onLogout={onLogout}
                isAuthenticated={isAuthenticated}
                userCuit={userCuit}
                isAdmin={isAdmin}
                onGoToAdmin={onGoToAdmin}
              />
            </motion.div>
          </motion.div>
        </section>

        <motion.section variants={fadeUp} className="mt-14 rounded-3xl border border-white/15 bg-white/5 p-6 backdrop-blur-2xl">
          <div className="mb-4 flex items-center gap-2 text-sm text-slate-200">
            <Sparkles className="h-4 w-4 text-cyan-300" /> Preview de escaneo inteligente
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
            <div className="relative mx-auto h-64 w-44 rounded-2xl border border-white/20 bg-slate-900/60 p-3">
              <div className="relative h-full w-full overflow-hidden rounded-xl border border-white/10 bg-white/5 p-3">
                <FileText className="h-8 w-8 text-cyan-200" />
                <div className="mt-3 space-y-2">
                  <div className="h-2 w-full rounded bg-white/20" />
                  <div className="h-2 w-4/5 rounded bg-white/15" />
                  <div className="h-2 w-3/5 rounded bg-white/10" />
                </div>
                <motion.div
                  className="pointer-events-none absolute left-2 right-2 h-12 rounded-lg bg-gradient-to-b from-cyan-300/45 via-blue-300/24 to-transparent blur-[1px]"
                  animate={{ top: ['-10%', '92%', '-10%'] }}
                  transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                  className="pointer-events-none absolute left-3 right-3 h-px bg-cyan-200/70"
                  animate={{ top: ['0%', '100%', '0%'] }}
                  transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl border border-white/20 bg-slate-900/40 p-4"
            >
              <p className="mb-3 text-xs uppercase tracking-[0.2em] text-cyan-200/80">Datos limpios</p>
              <div className="space-y-2 text-sm text-slate-200">
                {[
                  ['Periodo', '03/2011'],
                  ['CUIT Empleador', '30-12345678-9'],
                  ['Remuneracion', '$ 245.000,45'],
                  ['Estado', 'Validado'],
                ].map(([k, v], i) => (
                  <motion.div
                    key={k}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i, duration: 0.35 }}
                    className="grid grid-cols-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                  >
                    <span className="text-slate-400">{k}</span>
                    <span className="text-right font-medium text-slate-100">{v}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.section>

        <motion.section variants={fadeUp} className="mt-10 rounded-3xl border border-cyan-300/20 bg-slate-900/50 p-6 backdrop-blur-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/80">Por que elegir esta plataforma</p>
          <h3 className="mt-2 text-2xl font-semibold text-white">Resumen de funcionalidades que impulsan tu productividad pericial</h3>
          <p className="mt-3 max-w-3xl text-sm text-slate-300">
            Diseñada para estudios y peritos que necesitan velocidad, trazabilidad y resultados técnicos consistentes.
            Cada módulo está orientado a reducir horas operativas, minimizar errores y aumentar tu capacidad de respuesta.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              {
                icon: <Gauge className="h-4 w-4 text-cyan-200" />,
                title: 'Procesamiento forense acelerado',
                text: 'Ingesta de PDF ANSES, depuración y estructuración automática para pasar de documento bruto a datos accionables en minutos.',
              },
              {
                icon: <ScrollText className="h-4 w-4 text-cyan-200" />,
                title: 'Trazabilidad y auditoría técnica',
                text: 'Cada decisión y cada valor se preservan con contexto para respaldar dictámenes y revisiones con criterio pericial.',
              },
              {
                icon: <Lock className="h-4 w-4 text-cyan-200" />,
                title: 'Soporte con gestión de tickets',
                text: 'Seguimiento integral de incidencias con historial de interacciones, estado de avance y respuesta organizada.',
              },
              {
                icon: <CheckCircle2 className="h-4 w-4 text-cyan-200" />,
                title: 'Modelo operativo premium',
                text: 'Dashboard profesional, control de coins, experiencia mobile-first y flujo optimizado para escalar tu servicio.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl border border-white/15 bg-white/5 p-4">
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-white">{item.icon} {item.title}</p>
                <p className="mt-2 text-sm text-slate-300">{item.text}</p>
              </div>
            ))}
          </div>
        </motion.section>
      </motion.div>

      <div className="pointer-events-none absolute bottom-4 right-4 z-20 text-center">
        <img src="/imagen/Di-X.svg" alt="Di-X" className="brand-logo h-9 w-auto opacity-95" />
        <p className="mt-1 text-[11px] tracking-wide text-slate-300">Di-X</p>
      </div>

      <div className="relative z-20 border-t border-white/10 bg-slate-950/40 px-6 py-4 text-center text-[11px] text-slate-400">
        Disclaimer: esta plataforma brinda soporte técnico y herramientas de cálculo previsional con fines orientativos.
        La validación final del caso, la estrategia jurídica y la presentación judicial son responsabilidad exclusiva del profesional interviniente.
      </div>
    </div>
  )
}
